import { mergeWith, cloneDeep, isFinite, isPlainObject, omit } from "lodash"
import { SHARPE_RATIO } from "gatherers/single-date/period-performance"

import type { SingleDateGathererDataItem } from "gatherers/types"
import type { TupleN } from "helpers/types/helpers"

/**
 * Type guard with type predicate that checks if a value is a finite number.
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && isFinite(value)
}

/**
 * Extracts numeric value from summary field.
 * Summary can be either a number (legacy) or a metric object with a value property (new).
 */
function extractSummaryValue(
  summary: SingleDateGathererDataItem["summary"],
): number | null | undefined {
  if (summary === undefined || summary === null) return undefined
  if (typeof summary === "number") return summary
  if (isPlainObject(summary) && "value" in summary) {
    return summary.value
  }
  return undefined
}

/**
 * Adds a delta column comparing two data items.
 * Returns [original, comparison, delta] where delta = first - second for values.
 * For summaries: first - second when mode is originalVsComparison, otherwise second - first.
 * @param data - Tuple of exactly 2 gatherer data items for delta calculation
 * @param selectedMode - Optional compare mode to determine summary calculation
 * @returns Array of 3 items: original two plus delta item, maintaining Record structure
 */
export function addDeltaColumn(
  data: TupleN<SingleDateGathererDataItem, 2>,
  selectedMode?: string,
): TupleN<SingleDateGathererDataItem, 3> {
  const [first, second] = data

  const delta = mergeWith(
    cloneDeep(first),
    second,
    (existingValue, newValue) => {
      if (isFinite(newValue) && !isFinite(existingValue)) {
        return -newValue
      }
      return !isFinite(existingValue) || !isFinite(newValue)
        ? undefined
        : existingValue - newValue
    },
  )

  const isOriginalVsComparison = selectedMode === "originalVsComparison"
  const firstSummaryValue = extractSummaryValue(first.summary)
  const secondSummaryValue = extractSummaryValue(second.summary)

  const performanceSummaryDelta =
    isFiniteNumber(firstSummaryValue) && isFiniteNumber(secondSummaryValue)
      ? isOriginalVsComparison
        ? firstSummaryValue - secondSummaryValue
        : secondSummaryValue - firstSummaryValue
      : undefined

  const deltaValues = { ...delta.values }
  const sharpeMetric = deltaValues[SHARPE_RATIO]
  if (isPlainObject(sharpeMetric)) {
    deltaValues[SHARPE_RATIO] = omit(sharpeMetric, ["tooltip", "tooltipPlacement"])
  }

  return [
    first,
    second,
    {
      values: deltaValues,
      summary: performanceSummaryDelta,
    },
  ]
}
